import React, { useState, useCallback, useEffect, useMemo, act } from "react";
import { DatePicker, Dropdown, Input, Table, Button, message } from "antd";
import {
  SearchOutlined,
  BellOutlined,
  ConsoleSqlOutlined,
} from "@ant-design/icons";
import Layout from "../../components/Sidebar/Sidebar";
import styled from "styled-components";
import OrderColumns from "./OrderColumns";
import { ColumnsType } from "antd/es/table";
import { OrderDataType } from "./OrderColumns";
import { pdfjs } from "react-pdf";
import Icon from "../../components/Shared/Icon";
import { mockTableData } from "../../mockData/tableData";
import dayjs from "dayjs";
// Use the local worker from node_modules
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const Cases = () => {
  const [activeAudit, setActiveAudit] = useState("review_audit");
  const [correlationFilter, setCorrelationFilter] = useState<string | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const { RangePicker } = DatePicker;
  // Separate data states for each tab
  const [reviewData, setReviewData] = useState<any[]>([]);
  const [studentData, setStudentData] = useState<any[]>([]);
  const [radiologistData, setRadiologistData] = useState<any[]>([]);
  const [clinicalData, setClinicalData] = useState<any[]>([]);

  // Get the appropriate data based on active tab and filter
  const currentData = useMemo(() => {
    let data = [];
    switch (activeAudit) {
      case "review_audit":
        data = reviewData;
        break;
      case "student_audit":
        data = studentData;
        break;
      case "radiologist_audit":
        data = radiologistData;
        break;
      case "clinical_correlation":
        data = clinicalData;
        break;
      default:
        data = [];
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      data = data.filter((item) => {
        return (
          item.patient_name?.toLowerCase().includes(query) ||
          item.patient_id?.toLowerCase().includes(query) ||
          item.registration_no?.toLowerCase().includes(query) ||
          item.mod_study_id?.toLowerCase().includes(query) ||
          item.mod_study?.toLowerCase().includes(query) ||
          item.referring_phy?.toLowerCase().includes(query) ||
          item.hospital?.toLowerCase().includes(query)
        );
      });
    }

    // Apply correlation filter if active tab is clinical_correlation
    if (activeAudit === "clinical_correlation" && correlationFilter) {
      switch (correlationFilter) {
        case "success":
          data = data.filter(
            (item) => item.grade === "Yes" || item.grade === "No"
          );
          break;
        case "pending":
          data = data.filter((item) => item.grade === "-");
          break;
        case "uploaded":
          data = data.filter((item) => item.isDocumentUploaded === "Yes");
          break;
        case "all":
          data = clinicalData;
          break;
      }
    }

    // Apply date range filter
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      data = data.filter((item) => {
        const statusDate = dayjs(item.status_date);
        return statusDate.isAfter(startDate.subtract(1, 'day')) && statusDate.isBefore(endDate.add(1, 'day'));
      });
    }

    return data;
  }, [
    activeAudit,
    reviewData,
    studentData,
    radiologistData,
    clinicalData,
    correlationFilter,
    searchQuery,
    dateRange,
  ]);

  const handleDocumentUpload = async (recordId: number, file: File) => {
    try {
      // Create a FormData object to send the file
      const formData = new FormData();
      formData.append("file", file);
      formData.append("recordId", recordId.toString());

      // Here you would typically make an API call to upload the file
      // const response = await axios.post('/api/upload-document', formData);

      // Update the clinicalData state to reflect the new document status
      setClinicalData((prevData) =>
        prevData.map((item) =>
          item.id === recordId
            ? {
                ...item,
                isDocumentUploaded: "Yes",
                documentPath: URL.createObjectURL(file), // This creates a temporary URL for the file
              }
            : item
        )
      );

      // Also update the currentData through the filter
      if (correlationFilter === "uploaded") {
        setCorrelationFilter("uploaded"); // This will trigger a re-filter
      }

      message.success("Document uploaded successfully");
    } catch (error) {
      message.error("Failed to upload document");
      console.error("Upload error:", error);
    }
  };

  const columns: ColumnsType<OrderDataType> = OrderColumns({
    data: currentData,
    activeTab: activeAudit,
    onDocumentUpload: handleDocumentUpload,
  });

  useEffect(() => {
    const getUpdatedData = (data: OrderDataType[]): OrderDataType[] => {
      return data.map(item => {
        const savedGrade = localStorage.getItem(`grade_${item.id}`);
        if (savedGrade) {
          return { ...item, grade: savedGrade, aiGraded: false };
        }
        return item;
      });
    };

    const getUpdatedClinicalData = (data: OrderDataType[]): OrderDataType[] => {
      return data.map((item, index) => {
        // Load correlation data from localStorage with .cases suffix
        const savedCorrelationB = localStorage.getItem(`correlation_details_B_${item.id}.cases`);
        if (savedCorrelationB) {
          const correlationData = JSON.parse(savedCorrelationB);
          return { 
            ...item, 
            grade: correlationData.correlationStatus || "-",
            correlationComments: correlationData.comments || "",
            isCorrelated: true
          };
        }
        
        // Set different statuses for different cases based on index
        let status = "-"; // Default pending
        if (index % 3 === 0) {
          status = "Yes"; // Correlated
        } else if (index % 3 === 1) {
          status = "No"; // Mismatch
        } else {
          status = "-"; // Pending
        }
        
        // Set localStorage for cases with status
        if (status !== "-") {
          const mockCorrelationB = {
            correlationStatus: status,
            clinicalMatch: status === "Yes" ? "Match" : "Mismatch",
            comments: status === "Yes" ? "Report correlates well with clinical findings." : "Discrepancy noted between report and clinical presentation.",
          };
          localStorage.setItem(`correlation_details_B_${item.id}.cases`, JSON.stringify(mockCorrelationB));
        }
        
        return { 
          ...item, 
          grade: status, 
          isCorrelated: status !== "-"
        };
      });
    };

    setReviewData(getUpdatedData(mockTableData.filter((item) => item.type === "review")));
    setStudentData(getUpdatedData(mockTableData.filter((item) => item.type === "student")));
    setRadiologistData(getUpdatedData(mockTableData.filter((item) => item.type === "radiologist")));
    setClinicalData(getUpdatedClinicalData(mockTableData.filter((item) => item.type === "clinical")));
  }, []);

  const renderAuditOption = (label: string, key: string) => {
    const getDataCount = () => {
      switch (key) {
        case "review_audit":
          return reviewData.length;
        case "student_audit":
          return studentData.length;
        case "radiologist_audit":
          return radiologistData.length;
        case "clinical_correlation":
          return clinicalData.length;
        default:
          return 0;
      }
    };

    return (
      <h5
        onClick={() => setActiveAudit(key)}
        style={{
          cursor: "pointer",
          borderBottom: activeAudit === key ? "2px solid #635BFF" : "none",
          paddingBottom: "0.5rem",
          marginRight: "1rem",
          color: activeAudit === key ? "#635BFF" : "inherit",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          fontSize: "0.9rem",
          fontWeight: "500",
        }}
      >
        {label}
        <span
          style={{
            backgroundColor: "#f0f0f0",
            padding: "2px 6px",
            borderRadius: "12px",
            fontSize: "0.8rem",
            color: activeAudit === key ? "#635BFF" : "inherit",
          }}
        >
          {getDataCount()}
        </span>
      </h5>
    );
  };

  const handleClearLocalStorage = () => {
    localStorage.clear();
    message.success("Local storage has been cleared successfully!");
    
    // Optionally, you can reload the page to see the changes immediately
    window.location.reload();
  };

  const refreshClinicalData = () => {
    const getUpdatedClinicalData = (data: OrderDataType[]): OrderDataType[] => {
      return data.map((item, index) => {
        // Load correlation data from localStorage with .cases suffix
        const savedCorrelationB = localStorage.getItem(`correlation_details_B_${item.id}.cases`);
        if (savedCorrelationB) {
          const correlationData = JSON.parse(savedCorrelationB);
          return { 
            ...item, 
            grade: correlationData.correlationStatus || "-",
            correlationComments: correlationData.comments || "",
            isCorrelated: true
          };
        }
        
        // Set different statuses for different cases based on index
        let status = "-"; // Default pending
        if (index % 3 === 0) {
          status = "Yes"; // Correlated
        } else if (index % 3 === 1) {
          status = "No"; // Mismatch
        } else {
          status = "-"; // Pending
        }
        
        // Set localStorage for cases with status
        if (status !== "-") {
          const mockCorrelationB = {
            correlationStatus: status,
            clinicalMatch: status === "Yes" ? "Match" : "Mismatch",
            comments: status === "Yes" ? "Report correlates well with clinical findings." : "Discrepancy noted between report and clinical presentation.",
          };
          localStorage.setItem(`correlation_details_B_${item.id}.cases`, JSON.stringify(mockCorrelationB));
        }
        
        return { 
          ...item, 
          grade: status, 
          isCorrelated: status !== "-"
        };
      });
    };

    setClinicalData(getUpdatedClinicalData(mockTableData.filter((item) => item.type === "clinical")));
  };

  // Refresh clinical data when page loads
  useEffect(() => {
    refreshClinicalData();
  }, []);

  // Listen for storage changes to update clinical data
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && e.key.includes('correlation_details_B_') && e.key.includes('.cases')) {
        refreshClinicalData();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom storage events (for same-tab updates)
    const handleCustomStorageChange = () => {
      refreshClinicalData();
    };

    window.addEventListener('localStorageChange', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, []);

  return (
    <>
      <PageHeader>
        <HeaderLeft onClick={handleClearLocalStorage} style={{ cursor: 'pointer' }}>
          <RisContainer>
            <img
              src="/icons/iris.svg"
              alt="RIS Logo"
              width="auto"
              height={40}
              style={{ padding: "0.5rem", marginLeft: "1rem" }}
            />
          </RisContainer>
          <span>Case Study</span>
        </HeaderLeft>

        <HeaderRight>
          <SearchInput
            placeholder="Search by Name, Tags, Order ID"
            prefix={<SearchOutlined style={{ color: "#999" }} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
          />
          <BellOutlined style={{ marginLeft: "1rem" }} />
        </HeaderRight>
      </PageHeader>
      <PageContainer>
        <Layout />
        <ContentArea>
          <Tabs>
            <div className="audit-wrapper">
              <div className="audit-options">
                {renderAuditOption("Peer Review Audit", "review_audit")}
                {renderAuditOption("Student Audit", "student_audit")}
                {renderAuditOption("Second Opinion Audit", "radiologist_audit")}
                {renderAuditOption(
                  "Clinical Correlation",
                  "clinical_correlation"
                )}
              </div>
              <RangePicker 
                format="DD-MM-YYYY" 
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                allowClear
              />
            </div>
          </Tabs>
          {activeAudit === "clinical_correlation" && (
            <CorrelationContainer style={{ background: 'white', border: 'none', borderRadius: '0' }}>
              <CorrelationStatsContainer>
                <CorrelationStatBadge>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.2rem 0.8rem 0.2rem 0.8rem",
                      backgroundColor: "#fef2f2",
                      borderRadius: "20px",
                      border: "1px solid #fecaca",
                      width: "180px",
                      height: "24px"
                    }}
                  >
                    <div
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        backgroundColor: "#ef4444",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        flexShrink: 0
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="11" fill="#ef4444"/>
                        <polyline points="5,13 9,13 11,9 13,17 15,13 19,13" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        fontWeight: "600",
                        color: "#991b1b",
                        textAlign: "left",
                        flex: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      Critical: 66%
                    </div>
                  </div>
                </CorrelationStatBadge>
                
                <CorrelationStatBadge>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.2rem 0.8rem 0.2rem 0.8rem",
                      backgroundColor: "#f0f9ff",
                      borderRadius: "20px",
                      border: "1px solid #bae6fd",
                      width: "180px",
                      height: "24px"
                    }}
                  >
                    <div
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        backgroundColor: "#0ea5e9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        flexShrink: 0
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    </div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        fontWeight: "600",
                        color: "#0c4a6e",
                        textAlign: "left",
                        flex: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      Routine: 50%
                    </div>
                  </div>
                </CorrelationStatBadge>
              </CorrelationStatsContainer>
              
              <FilterSection style={{ paddingRight: '1.5rem' }}>
                <StyledDropdown
                  menu={{
                    items: [
                      {
                        key: "success",
                        label: "Complete Correlation",
                      },
                      {
                        key: "pending",
                        label: "Pending Correlation",
                      },
                      {
                        key: "document_uploaded",
                        label: "Uploaded Documents",
                      },
                      {
                        key: "all",
                        label: "All Cases",
                      },
                    ],
                    onClick: ({ key }) =>
                      setCorrelationFilter(
                        key === "success"
                          ? "success"
                          : key === "pending"
                          ? "pending"
                          : key === "document_uploaded"
                          ? "uploaded"
                          : "all"
                      ),
                  }}
                  placement="bottomRight"
                >
                  <EnhancedFilterButton>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
                      </svg>
                      {correlationFilter
                        ? correlationFilter === "success"
                          ? "Complete Correlation"
                          : correlationFilter === "pending"
                          ? "Pending Correlation"
                          : correlationFilter === "uploaded"
                          ? "Document Uploaded"
                          : "All Cases"
                        : "Correlation Status"}
                    </div>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  </EnhancedFilterButton>
                </StyledDropdown>
              </FilterSection>
            </CorrelationContainer>
          )}
          <CustomTable
            scroll={{ x: 850 }}
            columns={columns}
            dataSource={currentData}
            rowKey="id"
          />
        </ContentArea>
      </PageContainer>
    </>
  );
};

export default Cases;

const PageHeader = styled.div`
  width: 100%;
  height: 6vh;
  background:#f7f9fc;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
`;

const HeaderLeft = styled.h1`
  font-size: 1.2rem;
  font-weight: bold;
  align-items: center;
  justify-content: center;
  text-align: center;
  display: flex;
  width: 100%;
  margin-left: 24rem;
  gap: 0.5rem;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-right: 3rem;
`;

const SearchInput = styled(Input)`
  padding: 0.3rem 0.8rem;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 0.8rem;
  width: 18rem;
  gap: 0.5rem;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
`;

const PageContainer = styled.div`
  display: flex;
  height: calc(100vh - 6vh);
  overflow: hidden;
  margin-top: 6vh;
  margin-left: 2.5%;
`;

const Tabs = styled.div`
  width: 100%;
  padding: 0 0 0.1rem 0;
  margin-bottom: 0.1rem;

  .audit-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .audit-options {
    display: flex;
    gap: 1.5rem;
  }

  .ant-picker {
    min-width: 15rem;
  }
`;

const CustomTable = styled(Table<OrderDataType>)`
  width: 100%;
  table-layout: fixed;
  flex: 1;
  overflow: auto;

  .ant-table-thead > tr > th {
    background-color: #ede9fe;
    color: #000;
    font-weight: 600;
    text-align: left;
    padding: 0.5rem;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .ant-table-tbody > tr > td {
    padding: 0;
    height: 2.8rem;
    vertical-align: top;
  }

  .ant-table-body {
    overflow-y: auto !important;
    overflow-x: auto !important;
    max-height: calc(92vh - 200px) !important;
  }

  .ant-table-content {
    overflow: auto !important;
  }

  .ant-table-scroll {
    overflow: auto !important;
  }

  .ant-table-cell {
    padding: 0.5rem !important;
  }
`;

const ContentArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0.5rem 0.5rem 3rem;
  box-sizing: border-box;
  overflow-y: auto;
  height: 100%;
`;

const RisContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-right: 1rem;
  position: absolute;
  left: 0;
`;

const CorrelationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-top: 0.1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const CorrelationStatsContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const CorrelationStatBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
`;

const StyledDropdown = styled(Dropdown)`
  .ant-dropdown-menu {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .ant-dropdown-menu-item {
    padding: 8px 16px;
    font-size: 14px;
    color: #333;
    transition: all 0.3s;

    &:hover {
      background-color: #f5f5f5;
    }
  }
`;

const EnhancedFilterButton = styled(Button)`
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  height: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  min-width: 160px;
  justify-content: space-between;

  &:hover {
    background: #f9fafb;
    color: #111827;
    border-color: #9ca3af;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
`;
